import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    LinearProgress,
    Stack,
    Typography
} from "@mui/material";

import { Link as RouterLink } from "react-router-dom";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

import { useNavigate } from "react-router-dom";

import FacturaCard from "./FacturaCard";

export default function ProformaAccordion({

    grupo,
    envioId,
    folioInternoEnvio,
    puedeEditarColumna,
    onVerFactura,
    onVerConsolidado,
    onHabilitarProforma,
    onFinalizarProforma,
    onRevisarMrp

}) {

    const navigate = useNavigate();

    // 1. Deshabilitar botón si el usuario no tiene permisos
    const disabled = !puedeEditarColumna;

    // 2. Determinar texto del botón según estatus
    // (una vez "finalizada" ya no se muestra botón de acción: no hay reversión desde el frontend)
    let textoBoton = "Habilitar";
    let colorBoton = "success";

    if (grupo.estatus === "activa") {
        textoBoton = "Finalizar";
        colorBoton = "secondary";
    }

    const mostrarBotonAccion = grupo.estatus === "pendiente" || grupo.estatus === "activa";

    return (

        <Accordion
            sx={{
                mb: 2,
                backgroundColor: "#f0f0f0",
                boxShadow: 4,
                borderRadius: 4,
                "&:before": {
                    display: "none"
                }
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid
                    container
                    alignItems="center"
                    spacing={1}
                >
                    <Grid item md={3}>
                        <Stack>
                            <Typography
                                fontWeight="bold"
                                variant="h6"
                            >
                                <Inventory2Icon
                                    sx={{
                                        mr: 1,
                                        verticalAlign: "middle"
                                    }}
                                />
                                {grupo.titulo}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Proforma #{grupo.proforma_id}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Pedido #{grupo.pedido_id}
                            </Typography>
                        </Stack>
                    </Grid>

                    <Grid item md={1}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Facturas: {grupo.total_facturas}
                        </Typography>
                    </Grid>

                    <Grid item md={1}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Piezas: {grupo.total_piezas}
                        </Typography>
                    </Grid>

                    <Grid item md={1}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Enviar: {grupo.cantidad_producto_a_enviar}
                        </Typography>
                    </Grid>

                    <Grid item md={1}>
                        <Chip
                            label={grupo.estatus}
                            color={
                                grupo.estatus === "activa"
                                    ? "success"
                                    : grupo.estatus === "pendiente"
                                        ? "warning"
                                        : "default"
                            }
                        />
                    </Grid>

                    {/* Sección de Avance / Progreso */}
                    <Grid item md={2}>
                        <Box sx={{ width: "90%" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 0.5,
                                }}
                            >
                                <Typography variant="caption">Escaneo: {grupo.avance}%</Typography>
                                <Typography variant="caption">
                                    {grupo.cantidad_producto_empacada} / {grupo.cantidad_producto_a_enviar}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={Number(grupo.avance)}
                                sx={{ height: 6, borderRadius: 4, mb: 1 }}
                            />
                        </Box>

                        <Box sx={{ width: "90%" }}>
                            {(() => {
                                const facturada = Number(grupo.cantidad_facturada) || 0;
                                const cubierta = Number(grupo.cantidad_cubierta_excedente) || 0;
                                const aEnviar = Number(grupo.cantidad_a_enviar) || 0;
                                const contada = Number(grupo.cantidad_contada) || 0;

                                // 1. Determinar el total según la regla de negocio.
                                //    Disponible = lo facturado + lo cubierto con stock
                                //    interno de componentes. Si no hay ninguno de los
                                //    dos, usa aEnviar.
                                const disponible = facturada + cubierta;
                                const total = disponible > 0 ? disponible : aEnviar;

                                // 2. Calcular el porcentaje dinámico
                                const pct = total > 0
                                    ? Math.min(
                                        100,
                                        Math.max(
                                            0,
                                            Math.round((contada / total) * 100)
                                        )
                                    )
                                    : 0;

                                return (
                                    <>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography variant="caption">Surtido: {pct}%</Typography>
                                            <Typography variant="caption">
                                                {Math.round(contada)} / {Math.round(total)}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Number(pct)}
                                            sx={{ height: 6, borderRadius: 4 }}
                                        />
                                    </>
                                );
                            })()}
                        </Box>
                    </Grid>

                    {/* Sección de Botones de Acción */}
                    <Grid
                        item
                        md={3}
                    >
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            justifyContent="flex-end"
                        >
                            {/* Botón dinámico Habilitar / Finalizar (sin reversión desde el frontend una vez finalizada) */}
                            {mostrarBotonAccion && (
                                <Button
                                    variant="contained"
                                    color={colorBoton}
                                    size="small"
                                    disabled={disabled}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (disabled) return;

                                        if (grupo.estatus === "pendiente") {
                                            onHabilitarProforma?.(grupo);
                                        } else if (grupo.estatus === "activa") {
                                            onFinalizarProforma?.(grupo);
                                        }
                                    }}
                                >
                                    {textoBoton}
                                </Button>
                            )}

                            {/* Botón Surtir - Habilitado únicamente cuando el estatus es 'activa' */}
                            <Button
                                component={RouterLink}
                                to={`/surtido/${grupo.proforma_id}?envioId=${envioId}`}
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={grupo.estatus !== "activa"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                Surtir
                            </Button>

                            {/* Revisión de cantidades a enviar contra el MRP actual */}
                            {onRevisarMrp && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<InsightsOutlinedIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRevisarMrp(grupo);
                                    }}
                                >
                                    Revisar MRP
                                </Button>
                            )}

                            {/* Botón Consolidado */}
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onVerConsolidado(grupo);
                                }}
                            >
                                Consolidado
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </AccordionSummary>

            <AccordionDetails sx={{ borderRadius: 8 }}>
                <Divider sx={{ mb: 2, border: "1px solid #e0e0e0", borderRadius: 4 }} />
                {grupo.facturas.map((factura) => (
                    <FacturaCard
                        key={factura.factura_id}
                        factura={factura}
                        onVerDetalle={onVerFactura}
                    />
                ))}
            </AccordionDetails>
        </Accordion>

    );

}